import React, { useState } from "react";
import { Landmark } from "lucide-react";
import { bankAccountsApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";
import SearchableSelect from "../ui/SearchableSelect";

interface AddBankAccountModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultKind?: string;
}

const AddBankAccountModal: React.FC<AddBankAccountModalProps> = ({
  open,
  onClose,
  onCreated,
  defaultKind = "trust",
}) => {
  const [form, setForm] = useState({
    kind: defaultKind,
    name: "",
    institution: "",
    accountLast4: "",
    currency: "CAD",
  });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const resetForm = () => {
    setForm({ kind: defaultKind, name: "", institution: "", accountLast4: "", currency: "CAD" });
    setSaveError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      await bankAccountsApi.create(form);
      resetForm();
      onCreated();
      onClose();
    } catch (err: any) {
      setSaveError(err.response?.data?.error || "Failed to create account");
    } finally {
      setSaving(false);
    }
  };

  const kindOptions = [
    { value: "trust", label: "Trust Account" },
    { value: "operating", label: "Operating Account" },
    { value: "savings", label: "Savings Account" },
    { value: "credit_card", label: "Credit Card" },
    { value: "line_of_credit", label: "Line of Credit" },
  ];

  return (
    <PremiumModal
      open={open}
      onClose={handleClose}
      title="New Bank Account"
      icon={Landmark}
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="addBankAccountForm"
            disabled={saving || !form.name}
            className="px-5 py-2.5 rounded-xl text-white text-sm font-bold bg-brand hover:bg-brand-hover transition-colors disabled:opacity-50"
          >
            {saving ? "Creating..." : "Create Account"}
          </button>
        </div>
      }
    >
      {saveError && (
        <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{saveError}</div>
      )}

      <form id="addBankAccountForm" onSubmit={handleSave} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Account Type *</label>
          <SearchableSelect
            options={kindOptions}
            value={form.kind}
            onChange={(val) => setForm({ ...form, kind: val || "operating" })}
            placeholder="Select account type"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Account Name *</label>
          <input
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            placeholder="e.g. Main Operating Account"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Institution</label>
          <input
            type="text"
            value={form.institution}
            onChange={(e) => setForm({ ...form, institution: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            placeholder="e.g. TD Canada Trust"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Account Last 4 Digits
          </label>
          <input
            type="text"
            maxLength={4}
            value={form.accountLast4}
            onChange={(e) => setForm({ ...form, accountLast4: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
            placeholder="1234"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
          <SearchableSelect
            options={[
              { value: "CAD", label: "CAD" },
              { value: "USD", label: "USD" },
            ]}
            value={form.currency}
            onChange={(val) => setForm({ ...form, currency: val || "CAD" })}
            placeholder="Select currency"
          />
        </div>
      </form>
    </PremiumModal>
  );
};

export default AddBankAccountModal;
